import { useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { getErrorMessage } from '@/utils/errorUtils';

interface UseUserManagementInviteProps {
  selectedCustomerId: number | null;
  currentUserCustomerId: number | undefined;
  setError: (message: string) => void;
  setSuccess: (message: string) => void;
  refreshAllTabs: () => void;
}

export const useUserManagementInvite = ({
  selectedCustomerId,
  currentUserCustomerId,
  setError,
  setSuccess,
  refreshAllTabs,
}: UseUserManagementInviteProps) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleInviteUsers = useCallback(async (
    emails: string[], 
    role: 'admin' | 'user', 
    permission: 'readwrite' | 'readonly', 
    selectedCustomerIdForInvite?: number,
    authType?: 'password' | 'oauth',
    passwordType?: 'manual' | 'random',
    manualPassword?: string,
    sendEmail: boolean = true,
  ) => {
    const customerId = selectedCustomerIdForInvite || selectedCustomerId || currentUserCustomerId;

    if (!customerId) {
      setError('사용자를 초대하려면 먼저 고객사를 선택해주세요.');
      return;
    }

    setInviteLoading(true);
    setError('');
    setSuccess('');

    try {
      const perm = role === 'user' ? permission : undefined;
      const response = await adminAPI.inviteUsersBulk(
        emails, 
        customerId, 
        role, 
        perm,
        authType,
        passwordType,
        manualPassword,
        sendEmail,
      );
      
      const { invited_count, email_sent_count, email_failed_count, failed_emails, email_failed_emails } = response.data;

      let successMsg = `${invited_count}명의 사용자를 초대했습니다.`;
      if (email_sent_count > 0) {
        successMsg += ` 초대 메일 ${email_sent_count}건 발송 완료.`;
      }
      if (email_failed_count > 0) {
        successMsg += ` 초대 메일 ${email_failed_count}건 발송 실패: ${email_failed_emails.join(', ')}`;
      }
      setSuccess(successMsg);
      
      if (failed_emails && failed_emails.length > 0) {
        setError(`초대 실패: ${failed_emails.join(', ')}`);
      }
      
      if ((!failed_emails || failed_emails.length === 0) && email_failed_count === 0) {
        setShowInviteForm(false);
      }
      
      refreshAllTabs();
    } catch (error: unknown) {
      const errorMsg = getErrorMessage(error, '초대 실패');
      setError(`초대 실패: ${errorMsg}`);
    } finally {
      setInviteLoading(false);
    }
  }, [selectedCustomerId, currentUserCustomerId, setError, setSuccess, refreshAllTabs]);

  return {
    showInviteForm,
    setShowInviteForm,
    inviteLoading,
    handleInviteUsers,
  };
};

